import type { DocumentUploadLimitsBytes } from '../../app/config/runtime-config';

const SUPPORTED_EXTENSIONS = new Set(['pdf', 'docx', 'xlsx', 'csv', 'txt', 'png', 'jpg', 'jpeg', 'webp']);

export function extensionForFilename(filename: string): string {
  const index = filename.lastIndexOf('.');
  return index >= 0 ? filename.slice(index + 1).trim().toLowerCase() : '';
}

export function maximumFileSizeBytes(extension: string, limits: DocumentUploadLimitsBytes): number | null {
  const normalized = extension.toLowerCase().replace(/^\./, '');
  let typeLimit: number | null = null;
  if (normalized === 'pdf') typeLimit = limits.pdf;
  else if (normalized === 'docx') typeLimit = limits.docx;
  else if (normalized === 'xlsx') typeLimit = limits.xlsx;
  else if (normalized === 'csv') typeLimit = limits.csv;
  else if (normalized === 'txt') typeLimit = limits.txt;
  else if (normalized === 'png' || normalized === 'jpg' || normalized === 'jpeg' || normalized === 'webp') typeLimit = limits.image;
  if (typeLimit === null) return null;
  return Math.min(typeLimit, limits.absolute);
}

export function validateSelectedFile(file: File, limits: DocumentUploadLimitsBytes): string | null {
  const extension = extensionForFilename(file.name);
  if (!SUPPORTED_EXTENSIONS.has(extension)) {
    return 'Route42 supports PDF, DOCX, XLSX, CSV, TXT, PNG, JPG, JPEG, and WebP files.';
  }
  if (file.size <= 0) {
    return 'This file is empty. Choose a file that contains machine information.';
  }
  const maximum = maximumFileSizeBytes(extension, limits);
  if (maximum !== null && file.size > maximum) {
    return `${file.name} is larger than Route42’s ${formatMegabytes(maximum)} ${extension.toUpperCase()} limit. Choose a smaller file or split it into machine-specific sections.`;
  }
  return null;
}

export function uploadLimitGuidance(limits: DocumentUploadLimitsBytes): string {
  return [
    `PDF ${formatMegabytes(maximumFileSizeBytes('pdf', limits)!)}`,
    `DOCX ${formatMegabytes(maximumFileSizeBytes('docx', limits)!)}`,
    `XLSX ${formatMegabytes(maximumFileSizeBytes('xlsx', limits)!)}`,
    `CSV ${formatMegabytes(maximumFileSizeBytes('csv', limits)!)}`,
    `TXT ${formatMegabytes(maximumFileSizeBytes('txt', limits)!)}`,
    `images ${formatMegabytes(maximumFileSizeBytes('jpg', limits)!)}`,
  ].join(' · ');
}

export function formatMegabytes(bytes: number): string {
  const mib = bytes / (1024 * 1024);
  return `${Number.isInteger(mib) ? mib : mib.toFixed(1)} MB`;
}
